import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { checkEmailAssetsExist, uploadEmailAssets } from './signature-builder/utils/uploadEmailAssets';
import { toast } from 'sonner';

export const EmailAssetsUploader = () => {
  const [uploading, setUploading] = useState(false);
  const [assetsExist, setAssetsExist] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAssets = async () => {
      setChecking(true);
      const exists = await checkEmailAssetsExist();
      setAssetsExist(exists);
      setChecking(false);
    };
    checkAssets();
  }, []);

  const handleUpload = async () => {
    setUploading(true);
    try {
      const result = await uploadEmailAssets();
      if (result.success) {
        toast.success(`Successfully uploaded ${result.uploaded} email icons`);
        setAssetsExist(true);
      } else {
        toast.error(`Uploaded ${result.uploaded} icons, but ${result.failed} failed`);
      }
    } catch (error) {
      console.error('Error uploading email assets:', error);
      toast.error('Failed to upload email assets');
    } finally {
      setUploading(false);
    }
  };

  if (checking) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Email Signature Icons</CardTitle>
          <CardDescription>Checking icon status...</CardDescription>
        </CardHeader>
        <CardContent>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Email Signature Icons</CardTitle>
        <CardDescription>
          Upload contact and social media icons to ensure they display correctly in email clients.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {assetsExist ? (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span>Email icons are uploaded and ready</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              <span>Email icons need to be uploaded for proper display</span>
            </div>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Email Icons
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
